import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    Plus, FileText, CheckCircle, XCircle, Clock, AlertTriangle, 
    TrendingUp, Shield, Building2, Car, GraduationCap, Briefcase,
    User, DollarSign, Calendar, Eye, ArrowRight, Sparkles,
    ChevronRight, Loader2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { 
    getSolicitudes, crearSolicitud, aprobarSolicitud, convertirSolicitud,
    getClientes, analizarCliente
} from '../lib/api';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 2,
    }).format(value);
};

const tipoPrestamoConfig = {
    personal: { icon: User, label: 'Personal', color: 'bg-blue-500' },
    hipotecario: { icon: Building2, label: 'Hipotecario', color: 'bg-purple-500' },
    vehiculo: { icon: Car, label: 'Vehículo', color: 'bg-orange-500' },
    empresarial: { icon: Briefcase, label: 'Empresarial', color: 'bg-green-500' },
    consumo: { icon: DollarSign, label: 'Consumo', color: 'bg-yellow-500' },
    educativo: { icon: GraduationCap, label: 'Educativo', color: 'bg-pink-500' },
};

const estadoConfig = {
    pendiente: { icon: Clock, label: 'Pendiente', color: 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30' },
    aprobado: { icon: CheckCircle, label: 'Aprobado', color: 'bg-green-500/10 text-green-600 border-green-500/30' },
    rechazado: { icon: XCircle, label: 'Rechazado', color: 'bg-red-500/10 text-red-600 border-red-500/30' },
    condicional: { icon: AlertTriangle, label: 'Condicional', color: 'bg-orange-500/10 text-orange-600 border-orange-500/30' },
    convertido: { icon: Sparkles, label: 'Convertido', color: 'bg-primary/10 text-primary border-primary/30' },
};

const ScoreGauge = ({ score, maxScore = 100 }) => {
    const percentage = (score / maxScore) * 100;
    let color = 'text-red-500';
    let bg = 'bg-red-500';
    
    if (percentage >= 80) { color = 'text-green-500'; bg = 'bg-green-500'; }
    else if (percentage >= 65) { color = 'text-green-400'; bg = 'bg-green-400'; }
    else if (percentage >= 50) { color = 'text-yellow-500'; bg = 'bg-yellow-500'; }
    else if (percentage >= 35) { color = 'text-orange-500'; bg = 'bg-orange-500'; }
    
    return (
        <div className="flex flex-col items-center gap-2">
            <div className="relative w-24 h-24">
                <svg className="w-24 h-24 transform -rotate-90">
                    <circle
                        cx="48"
                        cy="48"
                        r="40"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        className="text-muted"
                    />
                    <circle
                        cx="48"
                        cy="48"
                        r="40"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${percentage * 2.51} 251`}
                        className={color}
                        strokeLinecap="round"
                    />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                    <span className={`text-2xl font-bold ${color}`}>{score}</span>
                </div>
            </div>
            <span className="text-sm text-muted-foreground">Score Crediticio</span>
        </div>
    );
};

export default function SolicitudesPage() {
    const { hasRole } = useAuth();
    const [solicitudes, setSolicitudes] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [aprobarDialogOpen, setAprobarDialogOpen] = useState(false);
    const [selectedSolicitud, setSelectedSolicitud] = useState(null);
    const [analyzing, setAnalyzing] = useState(false);
    const [analisisResult, setAnalisisResult] = useState(null);
    const [activeTab, setActiveTab] = useState('pendiente');
    const [submitting, setSubmitting] = useState(false);
    
    const [form, setForm] = useState({
        cliente_id: '',
        monto_solicitado: '',
        plazo_deseado: '',
        frecuencia: 'mensual',
        tipo_prestamo: 'personal',
        proposito: '',
        tiene_garantia: false,
        tipo_garantia: '',
        descripcion_garantia: '',
        valor_garantia: '',
        ingresos_mensuales: '',
        gastos_mensuales: '',
        otros_prestamos: '0',
        tiene_empleo: true,
        tipo_empleo: 'fijo',
        antiguedad_empleo_meses: '12',
    });

    const [aprobarForm, setAprobarForm] = useState({
        decision: 'aprobado',
        monto_aprobado: '',
        tasa_aprobada: '',
        plazo_aprobado: '',
        condiciones: '',
        motivo_rechazo: '',
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [solicitudesRes, clientesRes] = await Promise.all([
                getSolicitudes(),
                getClientes()
            ]);
            setSolicitudes(solicitudesRes.data);
            setClientes(clientesRes.data);
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleAnalizar = async () => {
        if (!form.cliente_id || !form.ingresos_mensuales) {
            toast.error('Complete los datos del cliente e ingresos');
            return;
        }
        
        setAnalyzing(true);
        try {
            const { data } = await analizarCliente(form.cliente_id, {
                cliente_id: form.cliente_id,
                ingresos_mensuales: parseFloat(form.ingresos_mensuales),
                gastos_mensuales: parseFloat(form.gastos_mensuales) || 0,
                tiene_empleo: form.tiene_empleo,
                tipo_empleo: form.tipo_empleo,
                antiguedad_empleo_meses: parseInt(form.antiguedad_empleo_meses) || 0,
                otros_prestamos: parseFloat(form.otros_prestamos) || 0,
            });
            setAnalisisResult(data);
            toast.success('Análisis completado');
        } catch (error) {
            toast.error('Error al analizar cliente');
        } finally {
            setAnalyzing(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const data = {
                ...form,
                monto_solicitado: parseFloat(form.monto_solicitado),
                plazo_deseado: parseInt(form.plazo_deseado),
                ingresos_mensuales: parseFloat(form.ingresos_mensuales),
                gastos_mensuales: parseFloat(form.gastos_mensuales) || 0,
                otros_prestamos: parseFloat(form.otros_prestamos) || 0,
                antiguedad_empleo_meses: parseInt(form.antiguedad_empleo_meses) || 0,
                valor_garantia: form.valor_garantia ? parseFloat(form.valor_garantia) : null,
            };
            await crearSolicitud(data);
            toast.success('Solicitud creada exitosamente');
            setDialogOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al crear solicitud');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setForm({
            cliente_id: '',
            monto_solicitado: '',
            plazo_deseado: '',
            frecuencia: 'mensual',
            tipo_prestamo: 'personal',
            proposito: '',
            tiene_garantia: false,
            tipo_garantia: '',
            descripcion_garantia: '',
            valor_garantia: '',
            ingresos_mensuales: '',
            gastos_mensuales: '',
            otros_prestamos: '0',
            tiene_empleo: true,
            tipo_empleo: 'fijo',
            antiguedad_empleo_meses: '12',
        });
        setAnalisisResult(null);
    };

    const handleAprobar = async () => {
        if (!selectedSolicitud) return;
        setSubmitting(true);
        try {
            await aprobarSolicitud(selectedSolicitud.id, {
                solicitud_id: selectedSolicitud.id,
                decision: aprobarForm.decision,
                monto_aprobado: aprobarForm.monto_aprobado ? parseFloat(aprobarForm.monto_aprobado) : null,
                tasa_aprobada: aprobarForm.tasa_aprobada ? parseFloat(aprobarForm.tasa_aprobada) : null,
                plazo_aprobado: aprobarForm.plazo_aprobado ? parseInt(aprobarForm.plazo_aprobado) : null,
                condiciones: aprobarForm.condiciones || null,
                motivo_rechazo: aprobarForm.motivo_rechazo || null,
            });
            toast.success(`Solicitud ${aprobarForm.decision === 'aprobado' ? 'aprobada' : aprobarForm.decision}`);
            setAprobarDialogOpen(false);
            loadData();
        } catch (error) {
            toast.error('Error al procesar la solicitud');
        } finally {
            setSubmitting(false);
        }
    };

    const handleConvertir = async (solicitud) => {
        try {
            const { data } = await convertirSolicitud(solicitud.id);
            toast.success(`Préstamo creado: ${formatCurrency(data.monto)}`);
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al convertir solicitud');
        }
    };

    const openAprobarDialog = (solicitud) => {
        setSelectedSolicitud(solicitud);
        setAprobarForm({
            decision: 'aprobado',
            monto_aprobado: solicitud.monto_solicitado.toString(),
            tasa_aprobada: solicitud.tasa_sugerida?.toString() || '',
            plazo_aprobado: solicitud.plazo_deseado.toString(),
            condiciones: '',
            motivo_rechazo: '',
        });
        setAprobarDialogOpen(true);
    };

    const filteredSolicitudes = solicitudes.filter(s => {
        if (activeTab === 'todas') return true;
        return s.estado === activeTab;
    });

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    const clientesActivos = clientes.filter(c => c.estado === 'activo');

    return (
        <div className="space-y-6" data-testid="solicitudes-page">
            {/* Header with Stats */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-2xl sm:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                            Solicitudes de Préstamo
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Gestiona y evalúa solicitudes de crédito
                        </p>
                    </div>
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-solicitud-btn" className="bg-gradient-to-r from-primary to-accent hover:opacity-90">
                                <Plus className="w-4 h-4 mr-2" />
                                Nueva Solicitud
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-heading flex items-center gap-2">
                                    <FileText className="w-5 h-5 text-primary" />
                                    Nueva Solicitud de Préstamo
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Tipo de Préstamo */}
                                <div className="space-y-3">
                                    <Label className="text-base font-semibold">Tipo de Préstamo</Label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {Object.entries(tipoPrestamoConfig).map(([key, config]) => {
                                            const Icon = config.icon;
                                            return (
                                                <button
                                                    key={key}
                                                    type="button"
                                                    onClick={() => setForm({ ...form, tipo_prestamo: key })}
                                                    className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                                                        form.tipo_prestamo === key
                                                            ? 'border-primary bg-primary/10'
                                                            : 'border-border hover:border-primary/50'
                                                    }`}
                                                    data-testid={`tipo-${key}`}
                                                >
                                                    <div className={`w-10 h-10 rounded-full ${config.color} flex items-center justify-center`}>
                                                        <Icon className="w-5 h-5 text-white" />
                                                    </div>
                                                    <span className="text-sm font-medium">{config.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Cliente y Monto */}
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label>Cliente</Label>
                                        <Select
                                            value={form.cliente_id}
                                            onValueChange={(value) => setForm({ ...form, cliente_id: value })}
                                        >
                                            <SelectTrigger data-testid="solicitud-cliente">
                                                <SelectValue placeholder="Seleccionar cliente" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {clientesActivos.map((cliente) => (
                                                    <SelectItem key={cliente.id} value={cliente.id}>
                                                        {cliente.nombre} - {cliente.cedula}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Monto Solicitado</Label>
                                        <Input
                                            type="number"
                                            value={form.monto_solicitado}
                                            onChange={(e) => setForm({ ...form, monto_solicitado: e.target.value })}
                                            placeholder="0.00"
                                            required
                                            data-testid="solicitud-monto"
                                        />
                                    </div>
                                </div>

                                <div className="grid gap-4 md:grid-cols-3">
                                    <div className="space-y-2">
                                        <Label>Plazo (períodos)</Label>
                                        <Input
                                            type="number"
                                            value={form.plazo_deseado}
                                            onChange={(e) => setForm({ ...form, plazo_deseado: e.target.value })}
                                            required
                                            min="1"
                                            data-testid="solicitud-plazo"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Frecuencia</Label>
                                        <Select
                                            value={form.frecuencia}
                                            onValueChange={(value) => setForm({ ...form, frecuencia: value })}
                                        >
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="diario">Diario</SelectItem>
                                                <SelectItem value="semanal">Semanal</SelectItem>
                                                <SelectItem value="quincenal">Quincenal</SelectItem>
                                                <SelectItem value="mensual">Mensual</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Propósito</Label>
                                        <Input
                                            value={form.proposito}
                                            onChange={(e) => setForm({ ...form, proposito: e.target.value })}
                                            placeholder="Ej: Capital de trabajo"
                                            data-testid="solicitud-proposito"
                                        />
                                    </div>
                                </div>

                                {/* Información Financiera */}
                                <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                                    <h4 className="font-semibold flex items-center gap-2">
                                        <TrendingUp className="w-4 h-4 text-primary" />
                                        Información Financiera del Cliente
                                    </h4>
                                    <div className="grid gap-4 md:grid-cols-3">
                                        <div className="space-y-2">
                                            <Label>Ingresos Mensuales</Label>
                                            <Input
                                                type="number"
                                                value={form.ingresos_mensuales}
                                                onChange={(e) => setForm({ ...form, ingresos_mensuales: e.target.value })}
                                                required
                                                data-testid="solicitud-ingresos"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Gastos Mensuales</Label>
                                            <Input
                                                type="number"
                                                value={form.gastos_mensuales}
                                                onChange={(e) => setForm({ ...form, gastos_mensuales: e.target.value })}
                                                data-testid="solicitud-gastos"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Otros Préstamos</Label>
                                            <Input
                                                type="number"
                                                value={form.otros_prestamos}
                                                onChange={(e) => setForm({ ...form, otros_prestamos: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="grid gap-4 md:grid-cols-3">
                                        <div className="space-y-2">
                                            <Label>Tipo de Empleo</Label>
                                            <Select
                                                value={form.tipo_empleo}
                                                onValueChange={(value) => setForm({ ...form, tipo_empleo: value })}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="fijo">Empleo Fijo</SelectItem>
                                                    <SelectItem value="temporal">Temporal</SelectItem>
                                                    <SelectItem value="independiente">Independiente</SelectItem>
                                                    <SelectItem value="pensionado">Pensionado</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Antigüedad (meses)</Label>
                                            <Input
                                                type="number"
                                                value={form.antiguedad_empleo_meses}
                                                onChange={(e) => setForm({ ...form, antiguedad_empleo_meses: e.target.value })}
                                            />
                                        </div>
                                        <div className="flex items-end">
                                            <Button 
                                                type="button" 
                                                variant="outline"
                                                onClick={handleAnalizar}
                                                disabled={analyzing || !form.cliente_id}
                                                className="w-full"
                                            >
                                                {analyzing ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <TrendingUp className="w-4 h-4 mr-2" />
                                                )}
                                                Analizar
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                {/* Resultado del Análisis */}
                                {analisisResult && (
                                    <Card className={`border-2 ${
                                        analisisResult.color === 'green' ? 'border-green-500 bg-green-50' :
                                        analisisResult.color === 'yellow' ? 'border-yellow-500 bg-yellow-50' :
                                        analisisResult.color === 'orange' ? 'border-orange-500 bg-orange-50' :
                                        'border-red-500 bg-red-50'
                                    }`}>
                                        <CardContent className="p-4">
                                            <div className="flex items-center gap-4">
                                                <ScoreGauge score={analisisResult.score} />
                                                <div className="flex-1">
                                                    <p className="font-bold text-lg">{analisisResult.recomendacion}</p>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        Capacidad de pago: {formatCurrency(analisisResult.capacidad_pago_mensual)}/mes
                                                    </p>
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {analisisResult.factores?.map((f, i) => (
                                                            <Badge key={i} variant="outline" className="text-xs">
                                                                {f.factor}: {f.estado} ({f.puntos}pts)
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Garantía */}
                                <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                                    <div className="flex items-center gap-2">
                                        <Shield className="w-4 h-4 text-primary" />
                                        <Label className="font-semibold">Garantía</Label>
                                        <input
                                            type="checkbox"
                                            checked={form.tiene_garantia}
                                            onChange={(e) => setForm({ ...form, tiene_garantia: e.target.checked })}
                                            className="ml-auto"
                                        />
                                    </div>
                                    {form.tiene_garantia && (
                                        <div className="grid gap-4 md:grid-cols-2">
                                            <div className="space-y-2">
                                                <Label>Tipo de Garantía</Label>
                                                <Select
                                                    value={form.tipo_garantia}
                                                    onValueChange={(value) => setForm({ ...form, tipo_garantia: value })}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Seleccionar" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="inmueble">Inmueble</SelectItem>
                                                        <SelectItem value="vehiculo">Vehículo</SelectItem>
                                                        <SelectItem value="electrodomestico">Electrodoméstico</SelectItem>
                                                        <SelectItem value="deposito">Depósito Bancario</SelectItem>
                                                        <SelectItem value="fiador">Fiador</SelectItem>
                                                        <SelectItem value="otro">Otro</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Valor Estimado</Label>
                                                <Input
                                                    type="number"
                                                    value={form.valor_garantia}
                                                    onChange={(e) => setForm({ ...form, valor_garantia: e.target.value })}
                                                />
                                            </div>
                                            <div className="space-y-2 md:col-span-2">
                                                <Label>Descripción</Label>
                                                <Textarea
                                                    value={form.descripcion_garantia}
                                                    onChange={(e) => setForm({ ...form, descripcion_garantia: e.target.value })}
                                                    placeholder="Describe la garantía..."
                                                    rows={2}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" disabled={submitting || !form.cliente_id} data-testid="solicitud-submit">
                                        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                        Crear Solicitud
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    {['pendiente', 'aprobado', 'rechazado', 'condicional', 'convertido'].map((estado) => {
                        const config = estadoConfig[estado];
                        const count = solicitudes.filter(s => s.estado === estado).length;
                        const Icon = config.icon;
                        return (
                            <Card 
                                key={estado} 
                                className={`cursor-pointer transition-all hover:scale-105 ${activeTab === estado ? 'ring-2 ring-primary' : ''}`}
                                onClick={() => setActiveTab(estado)}
                            >
                                <CardContent className="p-4 flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${config.color}`}>
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{count}</p>
                                        <p className="text-xs text-muted-foreground">{config.label}</p>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </div>

            {/* Tabs and Table */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="mb-4">
                    <TabsTrigger value="todas">Todas</TabsTrigger>
                    <TabsTrigger value="pendiente">Pendientes</TabsTrigger>
                    <TabsTrigger value="aprobado">Aprobadas</TabsTrigger>
                    <TabsTrigger value="rechazado">Rechazadas</TabsTrigger>
                </TabsList>

                <Card>
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Cliente</TableHead>
                                        <TableHead>Tipo</TableHead>
                                        <TableHead>Monto</TableHead>
                                        <TableHead className="hidden md:table-cell">Score</TableHead>
                                        <TableHead>Estado</TableHead>
                                        <TableHead className="w-32"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredSolicitudes.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} className="text-center py-12">
                                                <FileText className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                                <p className="text-muted-foreground">No hay solicitudes</p>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        filteredSolicitudes.map((solicitud) => {
                                            const tipoConfig = tipoPrestamoConfig[solicitud.tipo_prestamo] || tipoPrestamoConfig.personal;
                                            const estConfig = estadoConfig[solicitud.estado] || estadoConfig.pendiente;
                                            const TipoIcon = tipoConfig.icon;
                                            const EstIcon = estConfig.icon;
                                            
                                            return (
                                                <TableRow key={solicitud.id} className="animate-fadeIn">
                                                    <TableCell>
                                                        <div>
                                                            <p className="font-medium">{solicitud.cliente_nombre}</p>
                                                            <p className="text-xs text-muted-foreground">{solicitud.cliente_cedula}</p>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-2">
                                                            <div className={`w-8 h-8 rounded-full ${tipoConfig.color} flex items-center justify-center`}>
                                                                <TipoIcon className="w-4 h-4 text-white" />
                                                            </div>
                                                            <span className="text-sm">{tipoConfig.label}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <p className="font-mono font-bold">{formatCurrency(solicitud.monto_solicitado)}</p>
                                                        <p className="text-xs text-muted-foreground">{solicitud.plazo_deseado} {solicitud.frecuencia}</p>
                                                    </TableCell>
                                                    <TableCell className="hidden md:table-cell">
                                                        {solicitud.analisis?.score && (
                                                            <div className="flex items-center gap-2">
                                                                <Progress 
                                                                    value={solicitud.analisis.score} 
                                                                    className="w-16 h-2"
                                                                />
                                                                <span className="text-sm font-mono">{solicitud.analisis.score}</span>
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className={estConfig.color}>
                                                            <EstIcon className="w-3 h-3 mr-1" />
                                                            {estConfig.label}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => { setSelectedSolicitud(solicitud); setDetailDialogOpen(true); }}
                                                                title="Ver detalle"
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                            {solicitud.estado === 'pendiente' && hasRole(['admin', 'supervisor']) && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => openAprobarDialog(solicitud)}
                                                                    title="Procesar"
                                                                    className="text-primary"
                                                                >
                                                                    <CheckCircle className="w-4 h-4" />
                                                                </Button>
                                                            )}
                                                            {solicitud.estado === 'aprobado' && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="icon"
                                                                    onClick={() => handleConvertir(solicitud)}
                                                                    title="Convertir a préstamo"
                                                                    className="text-green-600"
                                                                >
                                                                    <ArrowRight className="w-4 h-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            </Tabs>

            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Detalle de Solicitud</DialogTitle>
                    </DialogHeader>
                    {selectedSolicitud && (
                        <div className="space-y-6">
                            <div className="flex items-start gap-4">
                                {selectedSolicitud.analisis && (
                                    <ScoreGauge score={selectedSolicitud.analisis.score} />
                                )}
                                <div className="flex-1 space-y-2">
                                    <h3 className="font-bold text-lg">{selectedSolicitud.cliente_nombre}</h3>
                                    <p className="text-sm text-muted-foreground">{selectedSolicitud.cliente_cedula}</p>
                                    <Badge variant="outline" className={estadoConfig[selectedSolicitud.estado]?.color}>
                                        {estadoConfig[selectedSolicitud.estado]?.label}
                                    </Badge>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div>
                                    <p className="text-sm text-muted-foreground">Monto Solicitado</p>
                                    <p className="font-mono font-bold">{formatCurrency(selectedSolicitud.monto_solicitado)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Plazo</p>
                                    <p className="font-medium">{selectedSolicitud.plazo_deseado} {selectedSolicitud.frecuencia}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Tasa Sugerida</p>
                                    <p className="font-medium">{selectedSolicitud.tasa_sugerida}%</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Cuota Estimada</p>
                                    <p className="font-mono font-bold text-primary">{formatCurrency(selectedSolicitud.cuota_estimada)}</p>
                                </div>
                            </div>

                            {selectedSolicitud.analisis && (
                                <Card className="bg-muted/50">
                                    <CardHeader className="pb-2">
                                        <CardTitle className="text-sm">Análisis Crediticio</CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-3">
                                        <p className="text-sm font-medium">{selectedSolicitud.analisis.recomendacion}</p>
                                        <div className="flex flex-wrap gap-2">
                                            {selectedSolicitud.analisis.factores?.map((f, i) => (
                                                <Badge key={i} variant="outline" className="text-xs">
                                                    {f.factor}: {f.estado} ({f.puntos}pts)
                                                </Badge>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Aprobar Dialog */}
            <Dialog open={aprobarDialogOpen} onOpenChange={setAprobarDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Procesar Solicitud</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Decisión</Label>
                            <Select
                                value={aprobarForm.decision}
                                onValueChange={(value) => setAprobarForm({ ...aprobarForm, decision: value })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="aprobado">Aprobar</SelectItem>
                                    <SelectItem value="condicional">Aprobación Condicional</SelectItem>
                                    <SelectItem value="rechazado">Rechazar</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {aprobarForm.decision !== 'rechazado' && (
                            <>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Monto Aprobado</Label>
                                        <Input
                                            type="number"
                                            value={aprobarForm.monto_aprobado}
                                            onChange={(e) => setAprobarForm({ ...aprobarForm, monto_aprobado: e.target.value })}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Tasa (%)</Label>
                                        <Input
                                            type="number"
                                            value={aprobarForm.tasa_aprobada}
                                            onChange={(e) => setAprobarForm({ ...aprobarForm, tasa_aprobada: e.target.value })}
                                            step="0.1"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Condiciones</Label>
                                    <Textarea
                                        value={aprobarForm.condiciones}
                                        onChange={(e) => setAprobarForm({ ...aprobarForm, condiciones: e.target.value })}
                                        placeholder="Condiciones especiales..."
                                        rows={2}
                                    />
                                </div>
                            </>
                        )}

                        {aprobarForm.decision === 'rechazado' && (
                            <div className="space-y-2">
                                <Label>Motivo de Rechazo</Label>
                                <Textarea
                                    value={aprobarForm.motivo_rechazo}
                                    onChange={(e) => setAprobarForm({ ...aprobarForm, motivo_rechazo: e.target.value })}
                                    placeholder="Explique el motivo..."
                                    rows={3}
                                    required
                                />
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setAprobarDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button 
                                onClick={handleAprobar}
                                disabled={submitting}
                                className={aprobarForm.decision === 'rechazado' ? 'bg-destructive hover:bg-destructive/90' : ''}
                            >
                                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                {aprobarForm.decision === 'aprobado' ? 'Aprobar' : 
                                 aprobarForm.decision === 'condicional' ? 'Aprobar con Condiciones' : 'Rechazar'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
